import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Trash2, Loader2, FileText, Image as ImageIcon, Eye } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export function AttachmentsModal({ open, onOpenChange, entityType, entityId, title }) {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const isAdmin = hasPermission("*");

  const load = () => {
    if (!entityId) return;
    setLoading(true);
    api.get(`/attachments?entity_type=${entityType}&entity_id=${entityId}`)
      .then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && entityId) load(); /* eslint-disable-next-line */ }, [open, entityId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("file", f);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado com sucesso.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const view = async (it) => {
    try {
      const res = await api.get(`/attachments/${it.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remover o anexo "${it.original_filename}"?`)) return;
    try { await api.delete(`/attachments/${it.id}`); toast.success("Anexo removido."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const isImg = (ct) => (ct || "").startsWith("image/");
  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="attachments-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Anexos {title ? `— ${title}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFile} className="hidden" data-testid="attachment-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-attachment-button"
            className="w-full border-2 border-dashed border-slate-200 rounded-xl py-6 flex flex-col items-center gap-2 text-slate-500 hover:border-brand hover:bg-brand-light/40 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin text-brand" /> : <Upload className="h-6 w-6 text-brand" />}
            <span className="text-sm font-medium">Anexar nota fiscal / boleto</span>
            <span className="text-xs text-slate-400">PDF, JPG, PNG ou WEBP (até 15 MB)</span>
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nenhum anexo ainda.</p>
          ) : items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`attachment-row-${it.id}`}>
              <div className="h-9 w-9 rounded-lg bg-white border border-slate-100 flex items-center justify-center shrink-0 text-brand">
                {isImg(it.content_type) ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{it.original_filename}</div>
                <div className="text-xs text-slate-400">{fmtSize(it.size)} · {fmtDate(it.created_at)} · {it.created_by_name || ""}</div>
              </div>
              <button onClick={() => view(it)} data-testid={`view-attachment-${it.id}`} className="p-1.5 rounded-md hover:bg-white text-slate-500" title="Visualizar"><Eye className="h-4 w-4" /></button>
              {isAdmin && <button onClick={() => remove(it)} data-testid={`delete-attachment-${it.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" title="Remover"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
